import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, TextField, MenuItem, Button,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { gpsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { getPageSubtitle } from '../../constants/pageSubtitles';

const ALERT_TYPES = [
  'overspeed', 'long_stop', 'device_offline', 'geofence_entry', 'geofence_exit',
  'ignition_on', 'ignition_off', 'unauthorized_movement',
];

const severityColor = (s: string) => {
  if (s === 'high') return 'error';
  if (s === 'medium') return 'warning';
  return 'default';
};

export const GPSAlertsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { formatDate } = useLocale();
  const { showToast } = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.alertType = typeFilter;
      const res = await gpsAPI.getAlerts(params);
      setRows(res.data || []);
    } catch {
      showToast('Failed to load alerts', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const handleAck = async (id: string) => {
    try {
      await gpsAPI.acknowledgeAlert(id);
      showToast('Alert acknowledged', 'success');
      load();
    } catch {
      showToast('Action failed', 'error');
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await gpsAPI.resolveAlert(id);
      showToast('Alert resolved', 'success');
      load();
    } catch {
      showToast('Action failed', 'error');
    }
  };

  const columns = [
    {
      field: 'alertType',
      header: 'Type',
      render: (row: Record<string, unknown>) => (
        <Chip size="small" label={String(row.alertType).replace(/_/g, ' ')} variant="outlined" />
      ),
    },
    { field: 'title', header: 'Alert' },
    { field: 'message', header: 'Details' },
    {
      field: 'vehicle',
      header: 'Vehicle',
      render: (row: Record<string, unknown>) => {
        const v = row.vehicle as Record<string, unknown> | undefined;
        return v?.vehicleNumber ? String(v.vehicleNumber) : '—';
      },
    },
    {
      field: 'severity',
      header: 'Severity',
      render: (row: Record<string, unknown>) => (
        <Chip size="small" label={String(row.severity)} color={severityColor(String(row.severity))} />
      ),
    },
    { field: 'status', header: 'Status' },
    {
      field: 'createdAt',
      header: 'Time',
      render: (row: Record<string, unknown>) => formatDate(String(row.createdAt), { dateStyle: 'medium', timeStyle: 'short' }),
    },
    {
      field: '_actions',
      header: 'Actions',
      render: (row: Record<string, unknown>) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {row.status === 'active' && hasPermission('gps_tracking', 'edit') && (
            <Button size="small" onClick={() => handleAck(String(row._id))}>Ack</Button>
          )}
          {row.status !== 'resolved' && hasPermission('gps_tracking', 'edit') && (
            <Button size="small" onClick={() => handleResolve(String(row._id))}>Resolve</Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="GPS Alerts"
        subtitle={getPageSubtitle('gps_alerts')}
        icon={<NotificationsActiveIcon />}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="acknowledged">Acknowledged</MenuItem>
          <MenuItem value="resolved">Resolved</MenuItem>
        </TextField>
        <TextField select size="small" label="Alert Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Types</MenuItem>
          {ALERT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        getRowId={(row) => String(row._id)}
        selectable={false}
        emptyMessage="No GPS alerts."
      />
    </Box>
  );
};
